import {
  BackendPlatformRole,
  BackendProjectDTO,
  BackendProjectUserDTO,
  BackendUserDTO,
  BackendUserMeDTO,
  BackendUserProjectDTO,
} from './onboardingTypes';
import {
  Organization,
  PlatformRole,
  Project,
  ProjectProvisionStatus,
  User,
  UserOrganizationMembership,
  normalizeProjectProvisionStatus,
} from '../types/onboarding';

/**
 * Role naming convention (API + UI):
 * - is_platform_admin: global Admin
 * - org_role: MAINTAINER | DEVELOPER in an organization (null for Admin)
 * - project_role: role on a project assignment only (project_users)
 * - User.platformRole (frontend): derived effective role for permissions/UI
 */

export function normalizeRole(
  role: BackendPlatformRole | string | null | undefined
): PlatformRole {
  const upper = (role || 'DEVELOPER').toString().toUpperCase();
  if (upper === 'ADMIN') return 'admin';
  if (upper === 'MAINTAINER') return 'maintainer';
  return 'developer';
}

export function denormalizeRole(role: PlatformRole): BackendPlatformRole {
  if (role === 'admin') return 'ADMIN';
  if (role === 'maintainer') return 'MAINTAINER';
  return 'DEVELOPER';
}

export function effectiveRoleFromBackend(dto: {
  is_platform_admin?: boolean;
  org_role?: BackendPlatformRole | string | null;
}): PlatformRole {
  if (dto.is_platform_admin) return 'admin';
  return normalizeRole(dto.org_role);
}

function parseDate(value: string | null | undefined, fallback: Date): Date {
  if (!value) return fallback;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return fallback;
  return parsed;
}

export function userFromBackend(dto: BackendUserDTO): User {
  const now = new Date();
  const memberships: UserOrganizationMembership[] = (dto.memberships ?? []).map(
    (membership) => ({
      organizationId: membership.organization_id,
      organizationName: membership.organization_name,
      orgRole: normalizeRole(membership.org_role),
    })
  );
  return {
    id: String(dto.user_id),
    email: dto.email_id,
    firstName: dto.first_name ?? '',
    lastName: dto.last_name ?? '',
    phone: dto.phone_no ?? undefined,
    organization: dto.organisation_name ?? '',
    activeOrganizationId: dto.organization_id ?? undefined,
    isPlatformAdmin: dto.is_platform_admin,
    platformRole: effectiveRoleFromBackend(dto),
    memberships,
    createdAt: parseDate(dto.start_date ?? null, now),
    updatedAt: parseDate(dto.last_login ?? dto.start_date ?? null, now),
  };
}

export function organizationFromBackend(dto: {
  organization_id: number;
  organization_name: string;
  organization_type?: string;
}): Organization {
  return {
    id: String(dto.organization_id),
    name: dto.organization_name,
    type: dto.organization_type,
    createdAt: new Date(),
  };
}

export function userMeFromBackend(dto: BackendUserMeDTO): {
  currentUser: User;
  memberships: UserOrganizationMembership[];
} {
  const now = new Date();
  const memberships: UserOrganizationMembership[] = (dto.organizations ?? []).map(
    (org) => ({
      organizationId: org.organization_id,
      organizationName: org.organization_name,
      orgRole: normalizeRole(org.org_role),
    })
  );
  const currentUser: User = {
    id: String(dto.user_id),
    email: dto.email_id,
    firstName: dto.first_name ?? '',
    lastName: dto.last_name ?? '',
    phone: dto.phone_no ?? undefined,
    organization: dto.active_organization_name ?? '',
    activeOrganizationId: dto.active_organization_id ?? undefined,
    isPlatformAdmin: dto.is_platform_admin,
    platformRole: effectiveRoleFromBackend(dto),
    createdAt: now,
    updatedAt: now,
  };
  return { currentUser, memberships };
}

export type DashboardProjectRow = {
  project_id: number;
  project_name: string;
  project_start_date: string;
  client_org_name: string;
  provision_status?: ProjectProvisionStatus;
  provision_error?: string;
};

function provisionFieldsFromApi(dto: {
  provision_status?: string;
  provision_error?: string;
}): Pick<DashboardProjectRow, 'provision_status' | 'provision_error'> {
  return {
    provision_status: normalizeProjectProvisionStatus(dto.provision_status),
    provision_error: dto.provision_error?.trim() || undefined,
  };
}

export function backendProjectToIProject(
  dto: BackendProjectDTO
): DashboardProjectRow {
  return {
    project_id: dto.project_id,
    project_name: dto.project_name,
    project_start_date: dto.start_date,
    client_org_name: dto.organization_name,
    ...provisionFieldsFromApi(dto),
  };
}

export function projectToDashboardRow(project: Project): DashboardProjectRow {
  const start =
    project.startDate instanceof Date
      ? project.startDate.toISOString().slice(0, 10)
      : String(project.startDate ?? '');
  return {
    project_id: Number(project.id),
    project_name: project.name,
    project_start_date: start,
    client_org_name: project.organization,
    provision_status: project.provisionStatus,
    provision_error: project.provisionError,
  };
}

export function projectFromBackend(dto: BackendProjectDTO): Project {
  const now = new Date();
  const start = parseDate(dto.start_date, now);
  const { provision_status, provision_error } = provisionFieldsFromApi(dto);
  return {
    id: String(dto.project_id),
    name: dto.project_name,
    organization: dto.organization_name,
    provisionStatus: provision_status,
    provisionError: provision_error,
    assignedUsers: [],
    startDate: start,
    endDate: dto.end_date ? parseDate(dto.end_date, now) : undefined,
    createdAt: start,
    updatedAt: start,
  };
}

/** Map GET /user/projects row to dashboard Redux `IProject` shape. */
export function userProjectRowFromBackend(
  dto: BackendUserProjectDTO
): DashboardProjectRow {
  return {
    project_id: dto.project_id,
    project_name: dto.project_name,
    project_start_date: dto.project_start_date,
    client_org_name: dto.organization_name,
    ...provisionFieldsFromApi(dto),
  };
}

export function projectUserFromBackend(dto: BackendProjectUserDTO): User {
  const now = new Date();
  return {
    id: String(dto.user_id),
    email: dto.email_id,
    firstName: dto.first_name ?? '',
    lastName: dto.last_name ?? '',
    organization: dto.organisation_name ?? '',
    platformRole: normalizeRole(dto.project_role),
    createdAt: parseDate(dto.start_date, now),
    updatedAt: parseDate(dto.last_login ?? dto.start_date, now),
  };
}
